export type CatalogShowcaseTipo = 'clase' | 'servicio' | 'listado' | 'membresia' | 'producto';

export interface CatalogShowcaseItem {
  icon: string;
  /** Foto del ítem (p. ej. retail); si falta se usa `icon` en la miniatura. */
  imageUrl?: string;
  name: string;
  meta: string;
  price: string;
  tipo: CatalogShowcaseTipo;
}

export interface Industry {
  id: string;
  /** Segmento para URLs compartibles: `/demo/:slug` */
  slug: string;
  label: string;
  icon: string;
  color: string;
  accentLight: string;
  biz: string;
  tagline: string;
  sub: string;
  cta: string;
  /** Subdominio para la barra del navegador simulado */
  siteSlug: string;
  /** Ciudad para cabecera tipo catálogo público */
  ciudad: string;
  /** Código país para bandera (obtenerEmojiBandera) */
  pais: string;
  /** Pestañas como en catálogo según vertical (modules_config / categorías) */
  catTabs: string[];
  /** Ítems tipo ProductoCard en la cuadrícula */
  catalogItems: CatalogShowcaseItem[];
  /** Etiquetas bajo el hero (demo) */
  previewTags: string[];
  services: {
    icon: string;
    name: string;
    duration: string;
    price: string;
  }[];
  timeSlots: string[];
  fullSlots: string[];
  /** Días de abril 2026 con disponibilidad (1–30) */
  availableDays: number[];
}

export const INDUSTRIES: Industry[] = [
  {
    id: 'gym',
    slug: 'gym',
    label: 'Artes Marciales / Gym',
    icon: '🥋',
    color: '#e84e4e',
    accentLight: '#fde8e8',
    biz: 'Dragon Dojo',
    tagline: 'Clases de Karate, Jiu-Jitsu y Muay Thai',
    sub: 'Disciplina, comunidad y progreso medible. Reserva tu clase en segundos.',
    cta: 'Reservar clase',
    siteSlug: 'dragondojo.clientelo.co',
    ciudad: 'Medellín',
    pais: 'CO',
    catTabs: ['Clases', 'Membresías', 'Torneos'],
    catalogItems: [
      { icon: '🥋', name: 'Karate adultos', meta: 'Lun/Mié/Vie · 7am', price: '$89.900/mes', tipo: 'clase' },
      { icon: '🧒', name: 'Karate niños', meta: 'Mar/Jue · 4pm', price: '$79.900/mes', tipo: 'clase' },
      { icon: '🥊', name: 'Muay Thai', meta: 'Lun–Sáb', price: '$99.900/mes', tipo: 'clase' },
      { icon: '🎫', name: 'Membresía full', meta: 'Acceso ilimitado', price: '$149.900/mes', tipo: 'membresia' },
    ],
    previewTags: ['Membresías', 'Asistencia', 'Pagos', 'Belt tracking', 'Torneos'],
    services: [
      { icon: '🥋', name: 'Karate Adultos', duration: '60 min', price: '$89.900/mes' },
      { icon: '🧒', name: 'Karate Niños', duration: '45 min', price: '$79.900/mes' },
      { icon: '🥊', name: 'Muay Thai', duration: '60 min', price: '$99.900/mes' },
      { icon: '🎁', name: 'Clase de prueba', duration: '45 min', price: 'Gratis' },
    ],
    timeSlots: [
      '7:00am',
      '8:00am',
      '9:00am',
      '10:00am',
      '4:00pm',
      '5:00pm',
      '6:00pm',
      '7:00pm',
    ],
    fullSlots: ['7:00am', '10:00am', '6:00pm'],
    availableDays: [1, 2, 3, 5, 7, 8, 9, 10, 12, 14, 15, 16, 17, 19, 21, 22, 23, 24, 26, 28, 29, 30],
  },
  {
    id: 'inmobiliaria',
    slug: 'inmobiliaria',
    label: 'Inmobiliaria',
    icon: '🏠',
    color: '#1a5cff',
    accentLight: '#e8f0ff',
    biz: 'Propiedades Élite',
    tagline: 'Compra, vende o arrienda en Bogotá',
    sub: 'Asesores certificados y visitas coordinadas sin fricción.',
    cta: 'Agendar visita',
    siteSlug: 'propiedadeselite.clientelo.co',
    ciudad: 'Bogotá',
    pais: 'CO',
    catTabs: ['Propiedades', 'Proyectos', 'Asesorías'],
    catalogItems: [
      { icon: '🏢', name: 'Apto Chapinero 85m²', meta: '3 hab · parqueadero', price: '$580.000.000', tipo: 'listado' },
      { icon: '🏡', name: 'Casa Chía', meta: '4 hab · lote 250m²', price: '$920.000.000', tipo: 'listado' },
      { icon: '🔑', name: 'Visita guiada', meta: 'Agenda 45 min', price: 'Gratis', tipo: 'servicio' },
      { icon: '📋', name: 'Asesoría hipotecaria', meta: 'Virtual o presencial', price: '$120.000', tipo: 'servicio' },
    ],
    previewTags: ['Fichas de propiedad', 'Firma digital', 'Seguimiento leads', 'Comisiones'],
    services: [
      { icon: '🔑', name: 'Visita a propiedad', duration: '45 min', price: 'Gratis' },
      { icon: '📋', name: 'Asesoría de compra', duration: '60 min', price: 'Gratis' },
      { icon: '✍️', name: 'Firma de arrendamiento', duration: '90 min', price: 'Según plan' },
      { icon: '📊', name: 'Valuación', duration: '120 min', price: '$150.000' },
    ],
    timeSlots: ['9:00am', '10:30am', '12:00pm', '2:00pm', '3:30pm', '5:00pm'],
    fullSlots: ['12:00pm', '5:00pm'],
    availableDays: [2, 3, 4, 6, 7, 9, 10, 11, 13, 14, 16, 17, 18, 20, 21, 23, 24, 25, 27, 28, 30],
  },
  {
    id: 'educacion',
    slug: 'educacion',
    label: 'Academia / Educación',
    icon: '📚',
    color: '#0f9e75',
    accentLight: '#e6f7f2',
    biz: 'Academia Integral',
    tagline: 'Inglés, programación, habilidades blandas',
    sub: 'Clases grupales y personalizadas. Prueba gratis y sigue tu avance en la app.',
    cta: 'Reservar clase prueba',
    siteSlug: 'academiaintegral.clientelo.co',
    ciudad: 'Cali',
    pais: 'CO',
    catTabs: ['Cursos', 'Talleres', 'Material'],
    catalogItems: [
      { icon: '🇬🇧', name: 'Inglés B1 intensivo', meta: '8 semanas · virtual', price: '$420.000', tipo: 'clase' },
      { icon: '💻', name: 'Python desde cero', meta: 'Sábados 9am', price: '$380.000', tipo: 'clase' },
      { icon: '🎯', name: 'Pack CV + LinkedIn', meta: '1 sesión', price: '$89.900', tipo: 'servicio' },
      { icon: '📖', name: 'Membresía biblioteca digital', meta: '12 meses', price: '$59.900/año', tipo: 'membresia' },
    ],
    previewTags: ['Asistencia', 'Calificaciones', 'Certificados', 'Grupos', 'Pagos en cuotas'],
    services: [
      { icon: '🇬🇧', name: 'Clase prueba inglés', duration: '50 min', price: 'Gratis' },
      { icon: '💻', name: 'Clase prueba programación', duration: '60 min', price: 'Gratis' },
      { icon: '🎯', name: 'Asesoría carrera', duration: '45 min', price: '$79.900' },
      { icon: '🛠️', name: 'Workshop habilidades', duration: '3 h', price: '$49.900' },
    ],
    timeSlots: ['8:00am', '10:00am', '2:00pm', '4:00pm', '6:00pm'],
    fullSlots: ['10:00am'],
    availableDays: [1, 4, 5, 6, 8, 11, 12, 13, 15, 18, 19, 20, 22, 25, 26, 27, 29, 30],
  },
  {
    id: 'retail',
    slug: 'retail',
    label: 'Retail / Moda',
    icon: '🛍️',
    color: '#1A56DB',
    accentLight: '#e8f1fc',
    biz: 'Nexo Moda',
    tagline: 'Ropa, calzado y accesorios con envío a todo el país',
    sub: 'Catálogo con fotos, tallas y stock. Retiro en tienda, domicilio o asesoría personalizada.',
    cta: 'Agendar asesoría',
    siteSlug: 'nexomoda.clientelo.co',
    ciudad: 'Barranquilla',
    pais: 'CO',
    catTabs: ['Destacados', 'Ropa', 'Calzado', 'Accesorios'],
    catalogItems: [
      {
        icon: '👕',
        imageUrl:
          'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=400&q=80',
        name: 'Camiseta algodón premium',
        meta: 'Tallas S–XL · verde bosque',
        price: '$79.900',
        tipo: 'producto',
      },
      {
        icon: '👖',
        imageUrl:
          'https://images.unsplash.com/photo-1542272604-787c3835535d?auto=format&fit=crop&w=400&q=80',
        name: 'Jeans slim fit',
        meta: 'Tallas 28–38 · índigo',
        price: '$189.900',
        tipo: 'producto',
      },
      {
        icon: '👟',
        imageUrl:
          'https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=400&q=80',
        name: 'Zapatillas urbanas',
        meta: 'Unisex · tallas 36–44',
        price: '$249.900',
        tipo: 'producto',
      },
      {
        icon: '🎒',
        imageUrl:
          'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&w=400&q=80',
        name: 'Mochila urbana 18L',
        meta: 'Resistente al agua · negro',
        price: '$129.900',
        tipo: 'producto',
      },
    ],
    previewTags: ['SKU y tallas', 'Inventario', 'Factura + IVA', 'WhatsApp pedidos', 'Click & collect'],
    services: [
      { icon: '👗', name: 'Asesoría de estilo en tienda', duration: '45 min', price: 'Gratis' },
      { icon: '🛒', name: 'Compra asistida (personal shopper)', duration: '60 min', price: '$65.000' },
      { icon: '✂️', name: 'Ajuste express (bastilla / cintura)', duration: '30 min', price: '$35.000' },
      { icon: '📦', name: 'Click & collect — retiro de pedido', duration: '15 min', price: 'Gratis' },
    ],
    timeSlots: ['10:00am', '11:00am', '12:00pm', '2:00pm', '3:00pm', '4:00pm', '5:00pm', '6:00pm'],
    fullSlots: ['12:00pm', '5:00pm'],
    availableDays: [1, 2, 3, 5, 6, 8, 9, 10, 12, 14, 15, 16, 18, 19, 21, 22, 23, 25, 26, 28, 29, 30],
  },
  {
    id: 'celulares',
    slug: 'celulares',
    label: 'Celulares & Tech',
    icon: '📱',
    color: '#0284c7',
    accentLight: '#e0f2fe',
    biz: 'TechZone',
    tagline: 'Smartphones, accesorios y servicio técnico certificado',
    sub: 'Nuevos, reacondicionados y reparaciones con garantía. Cotiza por WhatsApp o agenda tu cita.',
    cta: 'Agendar servicio',
    siteSlug: 'techzone.clientelo.co',
    ciudad: 'Medellín',
    pais: 'CO',
    catTabs: ['Smartphones', 'Accesorios', 'Servicio técnico', 'Usados'],
    catalogItems: [
      {
        icon: '📱',
        imageUrl:
          'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&w=400&q=80',
        name: 'Smartphone 256 GB',
        meta: '5G · garantía 1 año',
        price: '$2.499.900',
        tipo: 'producto',
      },
      {
        icon: '📲',
        imageUrl:
          'https://images.unsplash.com/photo-1592899677978-121e94a46ae1?auto=format&fit=crop&w=400&q=80',
        name: 'Android gama media',
        meta: '128 GB · dual SIM',
        price: '$899.900',
        tipo: 'producto',
      },
      {
        icon: '🎧',
        imageUrl:
          'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=400&q=80',
        name: 'Audífonos Bluetooth',
        meta: 'ANC · 30 h batería',
        price: '$349.900',
        tipo: 'producto',
      },
      {
        icon: '🛡️',
        imageUrl:
          'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?auto=format&fit=crop&w=400&q=80',
        name: 'Kit protector + vidrio',
        meta: 'Compatibilidad por modelo',
        price: '$79.900',
        tipo: 'producto',
      },
    ],
    previewTags: ['IMEI / serial', 'Garantía', 'Repuestos', 'Planes datos', 'Trade-in'],
    services: [
      { icon: '🔧', name: 'Diagnóstico de falla', duration: '30 min', price: '$30.000' },
      { icon: '🖥️', name: 'Cambio de pantalla', duration: '60–90 min', price: 'Cotizar' },
      { icon: '💾', name: 'Respaldo y transferencia de datos', duration: '45 min', price: '$49.900' },
      { icon: '💬', name: 'Asesoría compra de equipo', duration: '30 min', price: 'Gratis' },
    ],
    timeSlots: ['9:00am', '10:00am', '11:30am', '2:00pm', '3:30pm', '5:00pm', '6:00pm'],
    fullSlots: ['11:30am', '5:00pm'],
    availableDays: [1, 2, 4, 5, 7, 8, 9, 11, 12, 14, 15, 16, 18, 19, 21, 22, 23, 25, 26, 28, 29, 30],
  },
  {
    id: 'salud-belleza',
    slug: 'belleza',
    label: 'Salud & Belleza',
    icon: '💅',
    color: '#db2777',
    accentLight: '#fce7f3',
    biz: 'Bloom Spa & Estética',
    tagline: 'Faciales, masajes, depilación',
    sub: 'Experiencias premium con profesionales certificados. Reserva tu cupo.',
    cta: 'Reservar cita',
    siteSlug: 'bloomspa.clientelo.co',
    ciudad: 'Cali',
    pais: 'CO',
    catTabs: ['Servicios', 'Productos', 'Paquetes'],
    catalogItems: [
      { icon: '✨', name: 'Facial hidratante', meta: '60 min · esteticista', price: '$85.000', tipo: 'servicio' },
      { icon: '💆', name: 'Masaje relajante', meta: '90 min', price: '$120.000', tipo: 'servicio' },
      { icon: '⚡', name: 'Depilación láser axilas', meta: 'Paquete 6 sesiones', price: '$280.000', tipo: 'servicio' },
      { icon: '🧴', name: 'Kit spa en casa', meta: 'Envío nacional', price: '$95.000', tipo: 'listado' },
    ],
    previewTags: ['Citas online', 'Recordatorios SMS', 'Historial cliente', 'Productos'],
    services: [
      { icon: '✨', name: 'Facial', duration: '60 min', price: '$85.000' },
      { icon: '💆', name: 'Masaje', duration: '90 min', price: '$120.000' },
      { icon: '⚡', name: 'Depilación láser', duration: '45 min', price: 'Desde $50.000' },
      { icon: '💅', name: 'Manicure', duration: '50 min', price: '$65.000' },
    ],
    timeSlots: ['9:00am', '10:00am', '11:00am', '1:00pm', '3:00pm', '4:00pm', '5:00pm'],
    fullSlots: ['11:00am', '4:00pm'],
    availableDays: [1, 2, 4, 5, 7, 8, 9, 11, 12, 14, 15, 16, 18, 19, 21, 22, 23, 25, 26, 28, 29, 30],
  },
  {
    id: 'consultoria',
    slug: 'consultoria',
    label: 'Servicios / Consultoría',
    icon: '📑',
    color: '#7c3aed',
    accentLight: '#f3e8ff',
    biz: 'Soluciones Contables CO',
    tagline: 'Contabilidad y nómina para PYMES',
    sub: 'Equipo DIAN-ready. Agenda tu consulta inicial sin costo.',
    cta: 'Agendar consulta',
    siteSlug: 'solucionescontables.clientelo.co',
    ciudad: 'Bogotá',
    pais: 'CO',
    catTabs: ['Consultoría', 'Nómina', 'Declaraciones'],
    catalogItems: [
      { icon: '💬', name: 'Consulta inicial PYME', meta: '30 min · sin costo', price: 'Gratis', tipo: 'servicio' },
      { icon: '📄', name: 'Declaración de renta persona natural', meta: 'Temporada 2026', price: '$180.000', tipo: 'servicio' },
      { icon: '👥', name: 'Nómina hasta 10 empleados', meta: 'Mensual', price: '$320.000/mes', tipo: 'membresia' },
      { icon: '🏢', name: 'Outsourcing contable', meta: 'Plan mediano', price: 'A convenir', tipo: 'listado' },
    ],
    previewTags: ['Facturación DIAN', 'Proyectos', 'Portal cliente', 'Firma digital'],
    services: [
      { icon: '💬', name: 'Consulta inicial', duration: '30 min', price: 'Gratis' },
      { icon: '📄', name: 'Declaración de renta', duration: 'Variable', price: '$180.000' },
      { icon: '👥', name: 'Asesoría nómina', duration: '60 min', price: '$150.000' },
      { icon: '🏢', name: 'Plan empresa', duration: '90 min', price: 'A convenir' },
    ],
    timeSlots: ['8:00am', '9:00am', '11:00am', '1:00pm', '3:00pm', '5:00pm'],
    fullSlots: ['8:00am', '3:00pm'],
    availableDays: [3, 4, 5, 7, 10, 11, 12, 14, 17, 18, 19, 21, 24, 25, 26, 28],
  },
];

/** Índice del sector para la pestaña inicial; `0` si el slug no existe. */
export function getIndustryIndexFromSlug(slug: string | undefined): number {
  if (!slug) return 0;
  const key = slug.toLowerCase().trim();
  const idx = INDUSTRIES.findIndex((ind) => ind.slug === key);
  return idx >= 0 ? idx : 0;
}
